import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { useLang } from "../lib/i18n";
import { compressImage, savePhoto, getPhoto, deletePhoto, setPhotoCaption } from "../lib/offline";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Camera, Loader2, Trash2, WifiOff } from "lucide-react";

const MAX = 3;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * Field photos for a daily entry. Online: uploaded right away.
 * Offline: compressed and kept on the device, sent at sync time.
 */
export function EntryPhotos({ photos, localPhotos, onChange }) {
  const { t } = useLang();
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [thumbs, setThumbs] = useState({});
  const [localCaps, setLocalCaps] = useState({});
  const setRemoteCaption = (path, caption) => onChange(photos.map((p) => (p.path === path ? { ...p, caption } : p)), localPhotos);
  const setLocalCaption = (id, caption) => { setLocalCaps((c) => ({ ...c, [id]: caption })); setPhotoCaption(id, caption); };

  useEffect(() => {
    let urls = [];
    (async () => {
      const map = {}; const caps = {};
      for (const id of localPhotos) {
        const rec = await getPhoto(id);
        if (rec?.blob) { map[id] = URL.createObjectURL(rec.blob); urls.push(map[id]); }
        caps[id] = rec?.caption || "";
      }
      setThumbs(map); setLocalCaps(caps);
    })();
    return () => urls.forEach((u) => URL.revokeObjectURL(u));
  }, [localPhotos]);

  const total = photos.length + localPhotos.length;

  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (total >= MAX) { toast.error(`${t("Maximum")} ${MAX} ${t("photos par saisie.")}`); return; }
    setBusy(true);
    try {
      const blob = await compressImage(file);
      if (navigator.onLine) {
        const fd = new FormData();
        fd.append("file", blob, "photo.jpg");
        const res = await api.post("/harmonized/entry/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
        onChange([...photos, { path: res.data.path, caption: "" }], localPhotos);
        toast.success(t("Photo ajoutée."));
      } else {
        const id = await savePhoto(blob);
        onChange(photos, [...localPhotos, id]);
        toast.success(t("Photo conservée sur l'appareil, envoyée à la synchronisation."));
      }
    } catch (err) {
      const id = await savePhoto(await compressImage(file)).catch(() => null);
      if (id) { onChange(photos, [...localPhotos, id]); toast.success(t("Photo conservée sur l'appareil, envoyée à la synchronisation.")); }
      else toast.error(t("Photo illisible."));
    } finally { setBusy(false); }
  };

  const removeRemote = (path) => onChange(photos.filter((p) => p.path !== path), localPhotos);
  const removeLocal = async (id) => { await deletePhoto(id); onChange(photos, localPhotos.filter((x) => x !== id)); };

  return (
    <section className="bg-white border border-[#E2E8F0] rounded-lg shadow-sm p-4 sm:p-5" data-testid="entry-photos">
      <h2 className="text-sm font-semibold text-slate-900 mb-1">{t("Photos de terrain")}</h2>
      <p className="text-xs text-slate-500 mb-4">
        {t("Jusqu'à 3 photos par saisie, compressées automatiquement. Ajoutez une courte légende sous chaque photo (lieu, activité, date). Visibles par vous, votre superviseur provincial et l'Administrateur, et annexées au PDF du rapport.")}
      </p>

      <div className="flex flex-wrap gap-4 mb-4">
        {photos.map((p) => (
          <div key={p.path} className="w-36" data-testid={`photo-remote-${p.path.split("/").pop()}`}>
            <div className="relative">
              <img src={`${BACKEND}/api/mr-files/${p.path}?auth=${localStorage.getItem("token")}`} alt={p.caption || ""}
                className="h-28 w-36 rounded-md object-cover border border-slate-200 bg-slate-100" />
              <button onClick={() => removeRemote(p.path)} aria-label={t("Supprimer")}
                className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-white border border-slate-200 shadow flex items-center justify-center">
                <Trash2 className="h-3.5 w-3.5 text-red-600" />
              </button>
            </div>
            <Input value={p.caption || ""} maxLength={120} onChange={(e) => setRemoteCaption(p.path, e.target.value)}
              placeholder={t("Courte légende…")} className="mt-1.5 h-8 text-xs" data-testid={`photo-caption-${p.path.split("/").pop()}`} />
          </div>
        ))}
        {localPhotos.map((id) => (
          <div key={id} className="w-36" data-testid={`photo-local-${id}`}>
            <div className="relative">
              {thumbs[id]
                ? <img src={thumbs[id]} alt="" className="h-28 w-36 rounded-md object-cover border border-slate-300 bg-slate-100" />
                : <div className="h-28 w-36 rounded-md bg-slate-100 border border-slate-300" />}
              <span className="absolute bottom-1 left-1 inline-flex items-center gap-1 rounded bg-[#0F172A]/90 px-1.5 py-0.5 text-[9px] font-semibold uppercase text-white">
                <WifiOff className="h-2.5 w-2.5" />{t("en attente")}
              </span>
              <button onClick={() => removeLocal(id)} aria-label={t("Supprimer")}
                className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-white border border-slate-200 shadow flex items-center justify-center">
                <Trash2 className="h-3.5 w-3.5 text-red-600" />
              </button>
            </div>
            <Input value={localCaps[id] || ""} maxLength={120} onChange={(e) => setLocalCaption(id, e.target.value)}
              placeholder={t("Courte légende…")} className="mt-1.5 h-8 text-xs" data-testid={`photo-caption-${id}`} />
          </div>
        ))}
      </div>

      <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={pick} data-testid="photo-input" />
      <Button type="button" variant="outline" className="h-11 sm:h-10 rounded-md w-full sm:w-auto"
        disabled={busy || total >= MAX} onClick={() => fileRef.current?.click()} data-testid="photo-add-btn">
        {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Camera className="h-4 w-4 mr-1.5" />}
        {t("Ajouter une photo")} ({total}/{MAX})
      </Button>
    </section>
  );
}

export default EntryPhotos;
